import React, {memo, ReactNode, RefObject, useEffect, useLayoutEffect, useRef, useState} from 'react';
import styled, {css, DefaultTheme} from 'styled-components';

import {ExtendedError} from '../../../../../../libs/shared/core/src/helpers/types/customErrors';
import {
  buildCleanReactInnerHtml,
  CleanHtml,
  insertCleanInnerHtml,
  iSolemnlySwearThisHtmlIsClean,
} from '../../../../../../libs/shared/core/src/helpers/typescript/cleanHtml';
import {reportToBugsnag} from '../../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {TimelineVariationsEnum} from '../../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {useRestoreConvertedColorsBeforeCopy} from '../../../../../../libs/shared/ui/src/styles/darkModeCopyHelpers';
import {convertToDarkMode} from '../../../../../../libs/shared/ui/src/styles/darkModeHelpers';
import {fonts} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {userHtmlAnchorStyles} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {expandedDarkTheme} from '../../../../../../libs/shared/ui/src/theme/dark/darkTheme';
import {lightTheme} from '../../../../../../libs/shared/ui/src/theme/light/lightTheme';
import {useThemeController} from '../../../../../../libs/shared/ui/src/theme/themeControllerContext';
import {isDarkTheme} from '../../../../../../libs/shared/ui/src/theme/themeTypeHelpers';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {searchableAttrsBody} from '../../../../../../srcCommon/inboxes/search/searchAttributesHelpers';
import {makeBlockquoteStyle} from '../../../../../../srcCommon/styles/editorStyles';
import {emailCssHacks} from '../../../../../../srcCommon/styles/emailCssHacks';
import {useVariableHeightContext} from '../../../../common/scroll/variableHeightContext';
import {
  emailViewerTableSelectionClass,
  tableCellSelectedClass,
  useTableSelection,
} from './helpers/useTableSelection';

/*
 * Constants.
 */

const CONTENT_CONTAINER_CLASSNAME = 'front-message-html';

/*
 * Props.
 */

interface MessageHtmlProps {
  id?: string;
  trustedHtml?: CleanHtml;
  children?: ReactNode;
  timelineVariation: TimelineVariationsEnum;
  hasNewSynchronousP3?: boolean;
  isInbound?: boolean;
}

/*
 * Style.
 */

interface StyledHtmlDivProps {
  $timelineVariation: TimelineVariationsEnum;
  $hasNewSynchronousP3: boolean;
  $isInbound?: boolean;
}
const StyledHtmlDiv = styled.div<StyledHtmlDivProps>`
  white-space: initial;
  font-family: ${fonts.system};
  line-height: 1.4;
  position: relative;
  z-index: 0;

  ${(p) => getHtmlDivStyles(p, p.theme)}

  overflow-wrap: break-word;

  *,
  *::after,
  *::before {
    box-sizing: content-box;
  }

  .front-blockquote {
    margin: 0;
    border-left: 1px solid ${(p) => p.theme.palette.grey.shade30};
    padding-left: 10px;
  }

  img:not([style]):not([height]) {
    max-width: 100%;
    height: auto;
  }

  /* Override user-agent's default styles to avoid impossible-to-read indentation in long threads when blockquotes don't have the "front-blockquote" class applied to them. */
  blockquote {
    margin-inline-end: 0;
    margin-inline-start: 0.2em;
  }

  /* Hide the the normal selection styling if a user is selecting in a table, 
  and select the individual cells instead */
  table.${emailViewerTableSelectionClass} *::selection {
    background: transparent;
  }
  table.${emailViewerTableSelectionClass} .${tableCellSelectedClass} {
    // If this isn't !important then inline table styling can override this
    // class, and it will appear as if the cells aren't selected.
    background-color: ${(p) => p.theme.palette.blue.shade30} !important;
  }

  /* Apply email/providers specific hacks ( :( ) */
  ${emailCssHacks}
`;
function getHtmlDivStyles(props: StyledHtmlDivProps, theme: DefaultTheme) {
  if (props.$timelineVariation === TimelineVariationsEnum.ASYNCHRONOUS || props.$hasNewSynchronousP3) {
    return css`
      color: ${theme.constants.black};

      ${userHtmlAnchorStyles}

      ul li::marker {
        color: ${theme.composerText};
      }

      blockquote:not([class]):not([style]) {
        ${makeBlockquoteStyle(theme.greys.shade50)}
      }
    `;
  }

  // Styles for SYNCHRONOUS channels below
  return css`
    color: ${props.$isInbound ? theme.constants.black : theme.constants.white};

    a,
    a:visited,
    a:active {
      color: ${props.$isInbound ? theme.interactiveText : theme.constants.white};
      text-decoration: underline;
    }

    a,
    a * {
      cursor: pointer;
    }

    a:hover {
      color: ${props.$isInbound ? theme.interactiveTextHover : theme.constants.white};
    }

    blockquote {
      margin: 0 0 0 7px;
      border-left: 3px solid ${theme.constants.white};
      padding-left: 10px;
    }
  `;
}

/*
 * Component.
 */

export const MessageHtml = memo<MessageHtmlProps>((props) => {
  const {elementRef} = useImageLoadDetection();
  const {onToggleLightThemeOverride} = useThemeController();

  useTableSelection(elementRef);

  // Synchronous channels outbound messages have blue backgrounds in both Light mode and
  // Dark mode, so we don't want useThemedHtml to invert the text color or add color style
  // on the element level. We are handling this color styling in StyledHtmlDiv, not on the
  // element level.
  const shouldIgnoreDarkMode =
    props.timelineVariation === TimelineVariationsEnum.SYNCHRONOUS &&
    !props.isInbound &&
    !props.hasNewSynchronousP3;
  const isContentDarkMode = useThemedHtml(
    elementRef,
    onToggleLightThemeOverride,
    props.id,
    props.trustedHtml,
    shouldIgnoreDarkMode,
  );

  // If an HTML content was provided, use it as content.
  if (props.trustedHtml) {
    return (
      <StyledHtmlDiv
        id={props.id}
        theme={isContentDarkMode ? expandedDarkTheme : lightTheme}
        ref={elementRef}
        dangerouslySetInnerHTML={buildCleanReactInnerHtml(wrapHtmlWithDarkModeContainer(props.trustedHtml))}
        {...searchableAttrsBody}
        $timelineVariation={props.timelineVariation}
        $hasNewSynchronousP3={Boolean(props.hasNewSynchronousP3)}
        $isInbound={props.isInbound}
      />
    );
  }

  // Otherwise, just use the children.
  return (
    <StyledHtmlDiv
      id={props.id}
      ref={elementRef}
      {...searchableAttrsBody}
      $timelineVariation={props.timelineVariation}
      $hasNewSynchronousP3={Boolean(props.hasNewSynchronousP3)}
      $isInbound={props.isInbound}
    >
      {props.children}
    </StyledHtmlDiv>
  );
});

// In light mode, prefers-color-scheme:dark styles look broken. In dark mode,
// we use our own custom color inverter. Because of that, we can always
// replace these styles.
function removePrefersColorScheme(ref: RefObject<HTMLElement>) {
  if (!ref.current) {
    return;
  }

  for (const styleNode of Array.from(ref.current.getElementsByTagName('style'))) {
    const styles = styleNode.textContent;
    if (styles) {
      try {
        // In some rare cases, users get "is not a function" errors here.
        // Since this isn't critical, catch the error and report to Bugsnag
        // for further investigation.
        styleNode.textContent = styles.replace(
          /prefers-color-scheme:\s*dark/gi,
          'prefers-color-scheme:not-supported',
        );
      } catch (e) {
        reportToBugsnag(new Error('removePrefersColorScheme error'), {
          metadata: {
            styleType: typeof styles,
          },
        });
      }
    }
  }
}

function useImageLoadDetection() {
  const elementRef = useRef<HTMLDivElement>(null);
  const {onElementLoaded} = useVariableHeightContext();

  // After each update, find all the images in our content.
  useEffect(() => {
    const currentBody = elementRef.current;
    if (!currentBody) {
      return;
    }

    // Find all the images in our content.
    const imageNodes = Array.from(currentBody.querySelectorAll('img'));
    imageNodes.forEach((imageNode) => {
      // Remove empty alt attributes from inline images from Front, because they break offline image placeholders.
      // TODO: Remove this temporary workaround when enough time has passed.
      if (imageNode.hasAttribute('front-cid') && imageNode.getAttribute('alt') === '') {
        imageNode.removeAttribute('alt');
      }

      // If the image is already loaded, notify now.
      if (imageNode.complete) {
        onElementLoaded();
        return;
      }

      // Otherwise, wait for the image to load.
      const onImageNodeLoaded = () => {
        imageNode.removeEventListener('load', onImageNodeLoaded);
        onElementLoaded();
      };
      imageNode.addEventListener('load', onImageNodeLoaded);
    });
  });

  return {elementRef};
}

// Wrapping the HTML with a div allows us to style top level text nodes
// without modifying the StyledHtmlDiv itself.
function wrapHtmlWithDarkModeContainer(html: string) {
  return iSolemnlySwearThisHtmlIsClean(`<div class="${CONTENT_CONTAINER_CLASSNAME}">${html}</div>`);
}

function useThemedHtml(
  ref: RefObject<HTMLElement>,
  onToggleLightThemeOverride: (arg: boolean) => void,
  id?: string,
  html?: string,
  shouldIgnoreDarkMode?: boolean,
) {
  const userTheme = useUserTheme();
  const isExpandedDarkTheme = isDarkTheme(userTheme);
  const [isContentDarkMode, setIsContentDarkMode] = useState(isExpandedDarkTheme);
  const previousHtml = useRef('');

  useRestoreConvertedColorsBeforeCopy(ref, isExpandedDarkTheme);

  useLayoutEffect(() => {
    if (!ref.current || !html) {
      previousHtml.current = '';
      return;
    }

    removePrefersColorScheme(ref);

    // Reverts the rendered email back to the original HTML and CSS.
    const revertDarkModeColoring = (container: HTMLElement) => {
      insertCleanInnerHtml(container, wrapHtmlWithDarkModeContainer(html));
      setIsContentDarkMode(false);
    };

    const handleLightModeTheme = (container: HTMLElement) => {
      // If the content has been converted to dark mode, but the user's theme has switched
      // to light mode, rewrite the innerHTML with the original.
      if (isContentDarkMode) {
        revertDarkModeColoring(container);
      }
    };

    const handleDarkModeTheme = (container: HTMLElement) => {
      // If the html has changed, we're dealing with a fresh DOM subtree. Otherwise,
      // if nothing has changed, use the isContentDarkMode boolean to avoid color inverting
      // the content a second time.
      if (previousHtml.current !== html || !isContentDarkMode) {
        try {
          convertToDarkMode(container, userTheme.message.background);
          setIsContentDarkMode(true);
        } catch (error) {
          // This case shouldn't happen. If it does, it's usually caused by polished
          // encountering unexpected strings in the computed styles returned by the
          // browser. Report and fallback to the light theme.
          reportToBugsnag(new ExtendedError('Failed to convert HTML message to dark mode', error), {
            metadata: {
              id,
            },
          });
          revertDarkModeColoring(container);
          onToggleLightThemeOverride(true);
        }
      }
    };

    if (isExpandedDarkTheme && !shouldIgnoreDarkMode) {
      handleDarkModeTheme(ref.current);
    } else {
      handleLightModeTheme(ref.current);
    }

    previousHtml.current = html;
  }, [
    html,
    isContentDarkMode,
    isExpandedDarkTheme,
    ref,
    userTheme.message.background,
    shouldIgnoreDarkMode,
    onToggleLightThemeOverride,
    id,
  ]);

  return isContentDarkMode;
}
